import { useEffect } from 'react';
import { useSubscription } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { NOTIFICATION_SUBSCRIPTION, NOTIFICATIONS_LIST, UNREAD_NOTIFICATIONS_COUNT } from '../graphql/notifications';
import { NOTIFICATION_SUBSCRIPTION_SIMPLE } from '../graphql/notifications-simple-subscription';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router';
import { handleNotificationNavigation } from '../utils/notificationNavigation';
import { 
  playNotificationSound, 
  showBrowserNotification, 
  shouldShowNotification,
  requestNotificationPermission 
} from '../utils/notificationUtils';

// Re-export the hooks from NotificationService
export {
  useNotifications,
  useUnreadCount,
  useNotificationsGroupedByStatus,
  useCreateNotification,
  useUpdateNotificationStatus,
  useClearNotification,
  useNotificationTemplates,
  useCreateNotificationFromTemplate
} from '../services/NotificationService';

// Hook for real-time notification subscription with WebSocket using 8base's built-in subscriptions
export const useNotificationSubscription = (userId) => {
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  
  // Request notification permission on mount
  useEffect(() => {
    if (userId) {
      requestNotificationPermission().then((granted) => {
        if (granted) {
          console.log('✅ Browser notification permission granted');
        }
      });
    }
  }, [userId]);

  // Add logging to debug
  useEffect(() => {
    if (userId) {
      console.log('🔔 Real-time WebSocket subscription initialized for userId:', userId);
    } else {
      console.log('⏸️ WebSocket subscription waiting for userId...');
    }
  }, [userId]);
  
  const { data, loading, error } = useSubscription(NOTIFICATION_SUBSCRIPTION_SIMPLE, {
    variables: {},
    skip: !userId, // Don't subscribe until we have a userId
    shouldResubscribe: true, // Auto-resubscribe on reconnection
    onSubscriptionData: ({ subscriptionData }) => {
      console.log('📥 Real-time notification received via WebSocket:', subscriptionData);
      
      // 8base subscription format: data.Notification.node contains the notification
      if (subscriptionData?.data?.Notification) {
        const { mutation, node: newNotification } = subscriptionData.data.Notification;
        
        // Filter by userId in frontend (since backend filter might not work)
        if (newNotification.userId?.id === userId) {
          console.log(`✅ Notification ${mutation} for current user:`, newNotification.title);
          
          // Only show toast for newly created notifications
          if (mutation === 'create') {
            // Check if we should show notifications (respect DND mode)
            if (shouldShowNotification()) {
              // Play sound based on notification mode
              const soundType = newNotification.mode?.toLowerCase() || 'info';
              playNotificationSound(soundType);
              
              // Show toast notification
              showToastNotification(newNotification, enqueueSnackbar, navigate);
              
              // Show browser notification (if permission granted)
              showBrowserNotification(
                newNotification,
                () => console.log('Browser notification closed'),
                (notification) => {
                  handleNotificationNavigation(notification, navigate);
                }
              );
            }
          }
          
          // Update cache directly instead of refetching to avoid loading spinner
          // The subscription data will automatically update the UI without showing loading states
          apolloClient.cache.evict({ fieldName: 'notificationsList' });
          apolloClient.cache.evict({ fieldName: 'notificationsCount' });
          apolloClient.cache.gc();
          
          // Old approach - commented out to avoid loading spinner
          // apolloClient.refetchQueries({
          //   include: [NOTIFICATIONS_LIST, UNREAD_NOTIFICATIONS_COUNT],
          // });
        } else {
          console.log('⏭️ Skipping notification for different user:', newNotification.userId?.id);
        }
      }
    },
    onError: (err) => {
      console.error('❌ WebSocket subscription error:', err.message);
      // Don't crash the app, just log the error
    }
  });

  useEffect(() => {
    if (error) {
      console.error('❌ WebSocket error (check backend configuration):', error.message);
      console.error('Full error:', error);
    }
    if (loading && userId) {
      console.log('⏳ Subscription initializing... (this is normal)');
    }
    // Note: GraphQL subscriptions stay in "loading" state while actively listening
    // This is expected behavior! They're ready once they don't have errors.
    if (!error && userId) {
      console.log('🎉 Real-time subscription active and listening!');
    }
  }, [error, loading, userId]);

  // Return connection status
  // Note: Subscriptions stay "loading" while listening, so we check for errors instead
  return { 
    data, 
    loading, 
    error,
    isConnected: !error && userId // True if subscription is active without errors
  };
};

// Enhanced toast notification function with notistack
const showToastNotification = (notification, enqueueSnackbar, navigate) => {
  console.log('New notification received:', notification);
  
  // Determine notification variant based on mode
  let variant = 'info';
  if (notification.mode === 'WARNING') variant = 'warning';
  if (notification.mode === 'ERROR') variant = 'error';
  if (notification.mode === 'SUCCESS') variant = 'success';
  
  // Create the message content with both title and description
  const messageContent = (
    <div style={{ maxWidth: '300px' }}>
      <div style={{ 
        fontWeight: 'bold', 
        fontSize: '14px',
        marginBottom: notification.description ? '4px' : '0'
      }}>
        {notification.title || 'New Notification'}
      </div>
      {notification.description && (
        <div style={{ 
          fontSize: '12px', 
          opacity: 0.8,
          lineHeight: '1.3',
          wordWrap: 'break-word'
        }}>
          {notification.description}
        </div>
      )}
    </div>
  );
  
  // Show toast notification with click action
  enqueueSnackbar(messageContent, {
    variant,
    autoHideDuration: 6000, // Slightly longer to read description
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    action: (key) => (
      <button
        onClick={() => {
          // Use smart navigation based on jsonRecord
          const navigated = handleNotificationNavigation(notification, navigate);
          
          // If smart navigation didn't work, fallback to relatedEntityUrl
          if (!navigated && notification.relatedEntityUrl && navigate) {
            const url = notification.relatedEntityUrl;
            const path = url.startsWith('http') ? new URL(url).pathname : url;
            navigate(path);
          }
          
          // Close the snackbar
          enqueueSnackbar('', { key, variant: 'default', autoHideDuration: 0 });
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: '12px',
          fontWeight: '600',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'transparent';
        }}
      >
        View
      </button>
    )
  });
};